import { PALETTE, fillPoly, scircle, wob } from './sketch.js';
import { VEHICLE_DEFS, VEHICLE_SIZE } from '../data/vehicles.js';

/**
 * 车怎么画。
 *
 * 约定和人物一致：(x, y) 是「脚底」那一点，车身立在 y 之上。
 * 车头朝局部 +X 方向，heading 为弧度（0 = 朝右），所以要先转坐标系再画。
 */
export function drawVehicle(ctx, x, y, o = {}) {
  const def = VEHICLE_DEFS[o.kind] || VEHICLE_DEFS.wreck;
  const { w: W, h: H } = VEHICLE_SIZE;
  const t = o.time || 0;
  const heading = Number.isFinite(o.angle) ? o.angle : (o.heading || 0);
  const broken = !!o.broken;
  const lift = def.hover ? 5 + Math.sin(t * 2.2) * 1.6 : 0;

  ctx.save();
  ctx.translate(x, y - lift);
  ctx.rotate(heading);

  // 影子：身子底下那一团，转了也不跟着转，所以画在旋转之前更稳——这里放在后面补。
  // 轮子
  const wheelY = -3;
  for (const wx of [-W * 0.3, W * 0.32]) {
    for (const wySide of [-H * 0.34, H * 0.34]) {
      ctx.save();
      ctx.translate(wx, wySide);
      ctx.globalAlpha = 1;
      fillPoly(ctx, [[-4, wheelY - 3], [4, wheelY - 3], [4, wheelY + 3], [-4, wheelY + 3]], '#3f3b36');
      ctx.restore();
    }
  }

  // 车身
  const bw = W, bh = H * 0.62;
  fillPoly(ctx, [
    [-bw / 2, -bh - 4], [bw / 2 - 3, -bh - 4], [bw / 2 + 1, -bh + 2],
    [bw / 2 + 1, -4], [-bw / 2, -4],
  ], def.body);
  ctx.strokeStyle = PALETTE.ink;
  ctx.lineWidth = 1.6;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(-bw / 2 + wob(1, 0.6), -bh - 4);
  ctx.lineTo(bw / 2 - 3, -bh - 4 + wob(2, 0.6));
  ctx.lineTo(bw / 2 + 1, -bh + 2);
  ctx.lineTo(bw / 2 + 1, -4);
  ctx.lineTo(-bw / 2, -4);
  ctx.closePath();
  ctx.stroke();

  // 驾驶舱 / 货厢：房车与货车是一整块篷子，其它只留个小顶
  if (def.id === 'rv' || def.id === 'truck') {
    fillPoly(ctx, [[-bw / 2 + 2, -bh - 13], [bw * 0.12, -bh - 13], [bw * 0.12, -bh - 4], [-bw / 2 + 2, -bh - 4]], def.roof);
    ctx.strokeStyle = PALETTE.inkSoft;
    ctx.lineWidth = 1.2;
    ctx.strokeRect(-bw / 2 + 2, -bh - 13, bw * 0.62, 9);
  } else {
    fillPoly(ctx, [[-bw * 0.22, -bh - 11], [bw * 0.16, -bh - 11], [bw * 0.2, -bh - 4], [-bw * 0.26, -bh - 4]], def.roof);
    ctx.strokeStyle = PALETTE.inkSoft;
    ctx.lineWidth = 1.1;
    ctx.beginPath();
    ctx.moveTo(-bw * 0.22, -bh - 11);
    ctx.lineTo(bw * 0.16, -bh - 11);
    ctx.lineTo(bw * 0.2, -bh - 4);
    ctx.lineTo(-bw * 0.26, -bh - 4);
    ctx.closePath();
    ctx.stroke();
  }

  // 侧板一道补过铁皮的痕迹（破车才有的补丁）
  if (def.tier <= 1) {
    fillPoly(ctx, [[-bw * 0.05, -bh], [bw * 0.2, -bh - 2], [bw * 0.2, -bh + 5], [-bw * 0.05, -bh + 6]], '#cbb894');
    ctx.strokeStyle = PALETTE.inkSoft;
    ctx.lineWidth = 0.8;
    ctx.globalAlpha = 0.7;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  // 车灯：晚上是暖黄，抛锚了就不亮
  const lit = !broken;
  scircle(ctx, bw / 2 - 1, -bh + 1, 2.2, {
    color: PALETTE.ink, width: 1, seed: 7,
    fill: lit ? 'rgba(217,180,91,0.95)' : 'rgba(120,115,105,0.5)',
  });

  ctx.restore();

  // 影子画在世界坐标里（不跟着车头转）
  ctx.save();
  ctx.globalAlpha = 0.16;
  ctx.fillStyle = '#000';
  ctx.beginPath();
  ctx.ellipse(x, y - 2 - lift * 0.4, W * 0.46, W * 0.16, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // 抛锚：机盖下一缕黑烟
  if (broken) {
    ctx.save();
    for (let i = 0; i < 3; i++) {
      const f = (t * 0.7 + i * 0.33) % 1;
      const px = x - 8 - i * 2 + wob(i + 11, 3);
      const py = y - 20 - f * 22;
      ctx.globalAlpha = 0.5 * (1 - f);
      ctx.fillStyle = '#5a5550';
      ctx.beginPath();
      ctx.arc(px, py, 2 + f * 4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  // 跑起来：身后一撮浮土
  if (o.moving && Math.abs(o.speed || 0) > 60) {
    ctx.save();
    for (let i = 0; i < 4; i++) {
      const f = ((t * 1.6 + i * 0.25) % 1);
      const back = -Math.cos(heading), side = -Math.sin(heading);
      const px = x + back * (14 + f * 16) + side * wob(i + 3, 6);
      const py = y - 2 - f * 6;
      ctx.globalAlpha = 0.4 * (1 - f);
      ctx.fillStyle = PALETTE.soil;
      ctx.beginPath();
      ctx.arc(px, py, 1.6 + f * 2.4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}

/** 没在开的车（停在车棚里）也画同一个函数，只是不转轮子不带烟 */
export function drawParkedVehicle(ctx, x, y, o = {}) {
  drawVehicle(ctx, x, y, { angle: o.angle || 0, moving: false, ...o });
}
